import React, { useState, FormEvent, useEffect } from 'react';
import { Container, Form, Button, Alert } from 'react-bootstrap';
import { auth, db } from '../services/firebase';
import { sendSignInLinkToEmail, signOut, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';

// Add function to log login attempts
const logLoginAttempt = async (email: string, success: boolean) => {
  try {
    const loginLogsRef = collection(db, 'loginLogs');
    await addDoc(loginLogsRef, {
      email,
      timestamp: new Date().toISOString(),
      success,
    });
  } catch (error) {
    console.error('Error logging login attempt:', error);
  }
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if the URL contains an email sign-in link
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let emailFromStorage = window.localStorage.getItem('emailForSignIn');
      
      if (!emailFromStorage) {
        // If no email in storage, ask user for it
        emailFromStorage = window.prompt('Please provide your email for confirmation');
      }

      if (emailFromStorage) {
        setLoading(true);
        signInWithEmailLink(auth, emailFromStorage, window.location.href)
          .then(() => {
            logLoginAttempt(emailFromStorage!, true); // Log successful login
            window.localStorage.removeItem('emailForSignIn');
            navigate('/dashboard');
          })
          .catch((error) => {
            logLoginAttempt(emailFromStorage!, false); // Log failed login
            setError('Error signing in with email link: ' + error.message);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setError('Error signing out: ' + errorMessage);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      // Check if email exists in members or super_users collection
      const membersRef = collection(db, 'members');
      const superUsersRef = collection(db, 'super_users');
      
      const [membersSnapshot, superUsersSnapshot] = await Promise.all([
        getDocs(query(membersRef, where('Email', '==', email.toLowerCase()))),
        getDocs(query(superUsersRef, where('email', '==', email.toLowerCase())))
      ]);

      if (membersSnapshot.empty && superUsersSnapshot.empty) {
        setError('No account exists with that email address.');
        await logLoginAttempt(email, false);
        setLoading(false);
        navigate('/login/check-email', { 
          state: { error: 'No account exists with that email address.' }
        });
        return;
      }

      // Configure sign-in link settings
      const actionCodeSettings = {
        url: window.location.origin + '/login/confirm',
        handleCodeInApp: true
      };

      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      await logLoginAttempt(email, true); // Log successful login link sent
      
      // Save the email for later use
      window.localStorage.setItem('emailForSignIn', email);
      
      // Show success message
      setError('');
      navigate('/login/check-email');
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      await logLoginAttempt(email, false); // Log failed login attempt
      setError('Error sending login link: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show error from location state if it exists
  React.useEffect(() => {
    const state = location.state as { error?: string };
    if (state?.error) {
      setError(state.error);
    }
  }, [location]);

  // If user is logged in, show current user info
  if (auth.currentUser) {
    return (
      <Container className="mt-5">
        <Alert variant="info">
          <div className="mb-3">
            You are currently logged in as <strong>{auth.currentUser.email}</strong>.{' '}
            <Alert.Link onClick={handleSignOut} style={{ cursor: 'pointer' }}>
              Click here to Log Out
            </Alert.Link>
          </div>
          <div className="d-grid gap-2 d-md-flex justify-content-md-end">
            <Button 
              variant="primary"
              onClick={() => navigate('/dashboard')}
            >
              View Dashboard
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: '400px', marginTop: '5px' }}>
      <div className="text-center mb-4">
        <img 
          src="/images/Grizzly_FullCabin.jpg" 
          alt="White Grizzly Cabin"
          style={{
            width: '50%',
            maxWidth: '300px',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
          }}
        />
      </div>
      <h2 className="text-center mb-2">Login</h2>
      <p className="text-center text-muted mb-4">For those attending White Grizzly Trip 2025</p>
      
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Email address</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <Form.Text className="text-muted">
            <i>The email used in the group thread.</i><br></br>
          </Form.Text>
          <div className="w-100 text-center mt-2">
            <Form.Text className="text-muted">
              We'll send you a magic link to log in.
            </Form.Text>
          </div>
        </Form.Group>

        <Button variant="primary" type="submit" disabled={loading} className="w-100">
          {loading ? 'Sending...' : 'Send Login Link'}
        </Button>
      </Form>

      <div className="text-center mt-4">
        <small className="text-muted">Created by Jared S.</small>
      </div>
    </Container>
  );
} 